import '../../styles/restScreens.css';
import { useId } from 'react';
import useDisclosureCardClick from '../shared/useDisclosureCardClick';
import {
    statusMeta, TONE_CLASS, tierTone, firstEmail, firstPhone,
    dealSummary, money, toneTextStyle, hasHumanReview, getRecentCardActivity,
} from './clientsConstants';
import { formatPhone } from '../../lib/phone';
import ReachedVia from './ReachedVia';
import CurrentActivityPeek from './CurrentActivityPeek';
import { ContactTimeZoneText } from './ContactTimeZoneField';
import { resolveContactTimeZone } from '../../lib/contactTimeZone';
import '../../styles/step2jCrm.css';
import { BTN, TILE } from '../ui/platformStandard';
import ClickToCallButton from '../communications/ClickToCallButton';

// Module 2 — the Tier-2 "Hybrid" peek card (UX Addendum §3).
//
// One destination, three doors: this is what a List row OR a Card tile opens on click, and
// it is also the resting state of the Hybrid view. Built once, used by all three.
//
// It opens IN PLACE — never a modal (WR-013). The card shows: photo upper-left · name ·
// position · company · Account # · tier · phone · quick actions, with the signature
// gradient rule beneath the identity.
//
// Tier 2 -> Tier 3 is a click-anywhere cascade on the card itself when a handler is passed —
// no literal "Show more" control remains (BL-229).
//
// showIdentity=false when the card opens UNDER a List row (Val, 2026-07-15: "the contact info
// in both the collapsed and Hybrid views duplicates... Hybrid should be an expansion of the
// already present contact info"). He is right: in List the row already carries name, position,
// company, account #, tier and status — repeating all six an inch below is noise, and it was
// most of the dead space. In Hybrid and Card view there is no row, so the card IS the
// identity and shows it. One card, two contexts, no second component.
//
// BL-177 Step 2j: in Card and Hybrid view the card wears the fixed PEACOCK strip on top (6 px, .peacock-band: the platform's signature
// gradient, clipped to the card's rounded corners). In Hybrid view the card is two halves - this card on the left and the "Current
// activity" peek on the right (the right half stacks under the left on a narrow card). `showStrip` / `activity` are passed only by
// those two views; under a List row the card is unchanged.
//
// NO `dark` PROP. Colour is named by ROLE and resolved in CSS: .dark on <html> swaps the
// --surface-*/--ink values underneath. A component that branches on the theme is a component
// deciding a colour — which is exactly how Module 2 ended up with 73 hardcoded hexes.

function Pill({ tone, children, title }) {
    return (
        <span title={title} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${TONE_CLASS[tone] || TONE_CLASS.muted}`} style={toneTextStyle(tone)}>
            {children}
        </span>
    );
}

function Photo({ contact, size = 'lg' }) {
    const dim = size === 'lg' ? 'w-14 h-14 text-lg rounded-xl' : 'w-8 h-8 text-xs rounded-lg';
    const initials = (contact?.name || '?')
        .split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

    if (contact?.photo_url) {
        return <img src={contact.photo_url} alt={contact.name || 'contact'}
                    className={`${dim} object-cover flex-shrink-0`} />;
    }
    // No photo yet. Enrichment (Gravatar + suggest-and-confirm) is BL-150 — until then,
    // initials rather than a broken image or a stock silhouette.
    return (
        <div className={`${dim} flex-shrink-0 flex items-center justify-center font-semibold
                         bg-[var(--surface-sunken)] border border-[var(--surface-border)]`} style={toneTextStyle('system')}>
            {initials}
        </div>
    );
}

// The signature gradient rule — fixed across every brand. See global.css.
function Rule() {
    return <div className="flow-rule h-px w-full my-3 opacity-80" />;
}

function primaryAddress(contact) {
    const methods = (contact?.contact_methods || []).filter(m => m?.kind === 'address' && (m.value || '').trim());
    const chosen = methods.find(m => m.is_primary) || methods[0];
    return chosen ? chosen.value.trim() : null;
}

// Val, Oct 25 2026: the Hybrid card is "critical contact info + a recent activity preview". Left half = who they are and every way to
// reach them; right half = what happened lately (RecentActivityPeek). Under a List row the card is the contact block only.
function ClientHybridCard({ account, onShowMore, onCollapseAll, showIdentity = true, showStrip = false, showActivity = false, activity, activityLoading = false }) {
    const contentId = useId();
    const { onClick, onDoubleClick, onKeyDown, onPointerDownCapture } = useDisclosureCardClick({
        onOpen: onShowMore,
        onClose: onCollapseAll,
    });
    const contact = account.primary_contact;
    const meta = statusMeta(account.status);
    const email = firstEmail(contact);
    const phone = firstPhone(contact);
    const address = primaryAddress(contact);
    const deal = dealSummary(account);
    const zone = resolveContactTimeZone(contact);
    const surface = showStrip ? TILE : 'rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)]';

    return (
        <div data-m6-layout="clients" data-m6-component="ClientHybridCard"
            role="group"
            tabIndex={0}
            className={`${surface} ${showStrip ? 'ia-tile3d--clip pt-[5px]' : ''} ${showActivity ? 'ia-hybrid-card flex flex-col' : ''}`}
            data-testid={showActivity ? 'client-hybrid-card' : undefined}
            data-tile={showStrip ? 'platform' : undefined}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onKeyDown={onKeyDown}
            onPointerDownCapture={onPointerDownCapture}
        >
            <div className={showActivity ? 'ia-hybrid-halves flex-1' : undefined}>
                <div className="min-w-0 p-4">
                    <div className="flex gap-3">
                        {showIdentity && <Photo contact={contact} size="lg" />}
                        <div className="min-w-0 flex-1">
                            <button type="button" aria-expanded={false} aria-controls={contentId}
                                onClick={onShowMore} className="text-left text-lg font-bold leading-tight text-[var(--ink)]">
                                {contact?.name || account.account_name}
                            </button>
                            {showIdentity && (
                                <div className="text-sm truncate" style={toneTextStyle('system')}>
                                    {contact?.position || <span className="text-[var(--ink-muted)]">—</span>}
                                </div>
                            )}
                            {showIdentity && (
                                <div className="text-sm truncate text-[var(--ink-muted)]">
                                    {account.account_name}
                                    {account.account_number ? <span className="font-mono"> · {account.account_number}</span> : <span> · No account number until this converts</span>}
                                </div>
                            )}
                        </div>
                    </div>

                    {showIdentity && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            {hasHumanReview(account) && (
                                <Pill tone="open" title="Requires human review before booking">Needs review</Pill>
                            )}
                            {account.tier && <Pill tone={tierTone(account.tier.family)}>{account.tier.name}</Pill>}
                            <Pill tone="muted" title="Account type">{account.account_kind === 'personal' || (!account.account_kind && account.is_business === false) ? 'Personal' : 'Business'}</Pill>
                            <Pill tone={meta.tone} title={meta.hint}>{meta.label}</Pill>
                        </div>
                    )}

                    {showIdentity && <Rule />}

                    <dl id={contentId} className="ia-hybrid-contact mt-1" data-testid="hybrid-contact">
                        {phone && <><dt>Phone</dt><dd>{formatPhone(phone)}</dd></>}
                        {email && <><dt>Email</dt><dd>{email}</dd></>}
                        {address && showIdentity && <><dt>Address</dt><dd>{address}</dd></>}
                        {zone && showIdentity && (
                            <><dt>Time zone</dt><dd data-testid="hybrid-time-zone"><ContactTimeZoneText timeZone={zone} /></dd></>
                        )}
                        {!phone && !email && <dd className="italic text-[var(--ink-muted)]">No phone or email on the primary contact</dd>}
                    </dl>

                    {contact?.id && <div className="mt-1"><ReachedVia contact={contact} compact /></div>}

                    {/* THE DEAL - the thing a List row has no column for. Absent when there is no open deal, and absent entirely for a tenant
                        without crm.pipeline, because opportunities' RLS returns nothing to them. */}
                    {deal && (
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">
                                {deal.count === 1 ? 'Open deal' : `${deal.count} open deals`}
                            </span>
                            <span className="text-sm font-semibold text-[var(--ink)]">{money(deal.total)}</span>
                            {deal.stage && <Pill tone="system">{deal.stage.name}</Pill>}
                            {deal.expected && (
                                <span className="text-[11px] text-[var(--ink-muted)]">closes {deal.expected}</span>
                            )}
                        </div>
                    )}

                    {/* Quick actions - only rendered when the thing they need exists. Platform 3D pills. */}
                    {(phone || email) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {phone && <ClickToCallButton accountId={account.id} clientPhone={phone} fallbackTel={phone} className={BTN} />}
                            {email && <a href={`mailto:${email}`} onClick={e => e.stopPropagation()} className={BTN}>Email</a>}
                        </div>
                    )}
                </div>
                {showActivity && <CurrentActivityPeek activity={activity} loading={activityLoading} recentInvoice={getRecentCardActivity(account)} />}
            </div>
        </div>
    );
}

export { Pill, Photo, Rule };
export default ClientHybridCard;
